import dayjs from "dayjs";

import {
  faCheck,
  faEdit,
  faUserAltSlash,
  faUserClock,
  faUserEdit
} from "@fortawesome/free-solid-svg-icons";

import {
  calcUtilitiesFromScheduleEndpoints,
  findScheduleEndpointsCommuteEmissionsTons
} from "@aclymatepackages/calcs/recurring";
import { calcAvgEmissionsPastSixMonths } from "@aclymatepackages/date-helpers";
import { mainTheme } from "@aclymatepackages/themes";
import { splitAggregatedTransactionsBreakdownSubcategories } from "@aclymatepackages/other-helpers";

import { useCachedFirebaseCrud, useCollectionDataListener } from "../firebase";
import { fetchOurApi } from "../utils/apiCalls";
import { getAccountId } from "../accountHelpers";
import useDisplayMeasurementSystem from "../hooks/displayMeasurementSystem";

export const PLACEHOLDER_EMAIL_SUFFIX = "@no-email.local";

export const isPlaceholderEmail = (email = "") =>
  email.toLowerCase().endsWith(PLACEHOLDER_EMAIL_SUFFIX);

export const displayableEmail = (email = "") =>
  isPlaceholderEmail(email) ? "No email provided" : email;

export const employeeStatuses = {
  confirmed: {
    id: "confirmed",
    icon: faCheck,
    tooltip:
      "We have all of the information that we need to track this employee's emissions",
    color: mainTheme.palette.secondary.main,
    severity: 2,
    name: "Confirmed Employees"
  },
  unconfirmed: {
    id: "unconfirmed",
    icon: faUserEdit,
    tooltip:
      "You've sent this employee a survey but they haven't filled it out yet. In the meantime, we will display default emissions for this employee.",
    color: mainTheme.palette.error.main,
    severity: 4,
    name: "Unconfirmed Employees"
  },
  incomplete: {
    id: "incomplete",
    icon: faEdit,
    tooltip:
      "This employee's information is incomplete. Please send them another survey. In the meantime, we will display their most recent emissions.",
    color: mainTheme.palette.error.light,
    severity: 3,
    name: "Incomplete Employees"
  },
  onLeave: {
    id: "onLeave",
    icon: faUserClock,
    tooltip: "This employee is currently on leave",
    color: mainTheme.palette.secondary.light,
    severity: 1,
    name: "Employees on Leave"
  },
  terminated: {
    id: "terminated",
    icon: faUserAltSlash,
    tooltip: "This employee no longer works for you",
    color: mainTheme.palette.error.dark,
    severity: 0,
    name: "Terminated Employees"
  }
};

export const sendEmployeeSurvey = async ({
  setSendSurveyLoading,
  employee
}) => {
  if (isPlaceholderEmail(employee?.email)) {
    return { skipped: true, reason: "placeholder-email" };
  }

  const companyId = getAccountId();

  if (setSendSurveyLoading) {
    setSendSurveyLoading(true);
  }

  await fetchOurApi({
    path: `/survey-emails/employee/invite`,
    method: "POST",
    data: {
      employee,
      companyId
    }
  });

  if (setSendSurveyLoading) {
    return setSendSurveyLoading(false);
  }

  return null;
};

const hasRequiredScheduleParameters = (employeeData, scheduledTime) => {
  return employeeData && scheduledTime;
};

const normalizeEmployeeData = (employeeData) => {
  return Array.isArray(employeeData) ? employeeData : [employeeData];
};

const hasEmployees = (employees) => {
  return employees.length > 0;
};

const hasRequiredEmployeeFields = (employee) => {
  return employee.id && employee.email && employee.name;
};

const validateEmployeeFields = (employee) => {
  if (!hasRequiredEmployeeFields(employee)) {
    throw new Error(
      "Invalid employee data: missing required fields (id, email, or name)"
    );
  }
  return {
    id: employee.id,
    email: employee.email,
    name: employee.name
  };
};

const buildScheduleApiPayload = (
  validatedEmployees,
  companyId,
  scheduledTime,
  timezone
) => {
  return {
    employees: validatedEmployees,
    companyId,
    scheduledTime,
    timezone: timezone || "America/Los_Angeles",
    status: "pending",
    createdAt: new Date().toISOString()
  };
};

const isApiResponseSuccess = (response) => {
  return response && !response.error;
};

export const scheduleSurveyDelivery = async ({
  employeeData,
  scheduledTime,
  timezone
}) => {
  const companyId = getAccountId();

  if (!hasRequiredScheduleParameters(employeeData, scheduledTime)) {
    throw new Error(
      "Missing required parameters for scheduling survey delivery"
    );
  }

  const employees = normalizeEmployeeData(employeeData);

  if (!hasEmployees(employees)) {
    throw new Error("No employees provided for survey scheduling");
  }

  const validatedEmployees = employees.map(validateEmployeeFields);

  const response = await fetchOurApi({
    path: `/survey-emails/employee/schedule`,
    method: "POST",
    data: buildScheduleApiPayload(
      validatedEmployees,
      companyId,
      scheduledTime,
      timezone
    )
  });

  if (!isApiResponseSuccess(response)) {
    throw new Error(response?.error || "Failed to schedule survey delivery");
  }

  return {
    success: true,
    scheduledJobId: response.jobId || response.scheduledJobId || response.id,
    scheduledTime: response.scheduledTime || scheduledTime,
    employeeCount: validatedEmployees.length,
    timezone: response.timezone || timezone || "America/Los_Angeles",
    ...response
  };
};

export const modifyEmployeeVehiclesInCommuteSchedules = ({
  commuteSchedules = [],
  newVehicle,
  updatedVehicles
}) =>
  commuteSchedules.map((commuteSchedule) => {
    const newCommuteEndpoint = commuteSchedule.commuteEndpoints.map(
      (commuteEndpoint) => {
        const { vehicles: commuteEndpointVehicles = [] } = commuteEndpoint;

        const newCommuteEndpointsVehicles = commuteEndpointVehicles.map(
          (commuteVehicle) => {
            const { id: commuteVehicleId } = commuteVehicle;
            const findCommuteVehicle = updatedVehicles
              ? updatedVehicles.find(
                  ({ id: vehicleId }) => vehicleId === commuteVehicleId
                )
              : newVehicle.id === commuteVehicleId;

            if (findCommuteVehicle) {
              const {
                name: modifiedCommuteVehicleName,
                tonsCo2ePerMile: modifiedCommuteVehicleTonsCo2ePerMile
              } = updatedVehicles ? findCommuteVehicle : newVehicle;

              return {
                ...commuteVehicle,
                id: commuteVehicleId,
                name: modifiedCommuteVehicleName,
                tonsCo2ePerMile: modifiedCommuteVehicleTonsCo2ePerMile
              };
            }

            return commuteVehicle;
          }
        );

        const commuteEndpointVehiclesUpdateObj =
          newCommuteEndpointsVehicles.length
            ? { vehicles: newCommuteEndpointsVehicles }
            : {};

        return {
          ...commuteEndpoint,
          ...commuteEndpointVehiclesUpdateObj
        };
      }
    );

    return {
      ...commuteSchedule,
      commuteEndpoints: newCommuteEndpoint
    };
  });

export const buildNewEmployeeStatusLinks = (
  { links },
  newStatus,
  givenCompanyId
) => {
  const companyId = getAccountId();

  return links.map((link) =>
    link.id === givenCompanyId || companyId
      ? { ...link, employeeStatus: newStatus }
      : link
  );
};

export const useEmployeeStatuses = () => {
  const companyId = getAccountId();
  const [employees, employeesLoading] = useCollectionDataListener("employees");

  const employeesWithStatuses = employees.map((employee = {}) => {
    const { links = [] } = employee;
    const { employeeStatus } = links.find(({ id }) => id === companyId) || {};

    return { ...employee, status: employeeStatus };
  });

  return [employeesWithStatuses, employeesLoading];
};

export const useFindCurrentEmployeeEmissions = () => {
  const [employeesAggregatedTransactions] = useCollectionDataListener(
    "employees-aggregated-transactions"
  );
  const { convertCarbonUnits } = useDisplayMeasurementSystem();

  return ({ employees, emissionsMonths, employeeCount }) => {
    if (!employees.length) {
      return [];
    }

    const filterEmployeesByStatus = (status) =>
      employees.filter((employee) => employee.status === status);

    const confirmedEmployees = filterEmployeesByStatus("confirmed");
    const unconfirmedEmployees = filterEmployeesByStatus("unconfirmed");
    const terminatedEmployees = filterEmployeesByStatus("terminated");
    const onLeaveEmployees = filterEmployeesByStatus("onLeave");
    const incompleteEmployees = filterEmployeesByStatus("incomplete");

    const notUnconfirmedEmployeesCount =
      employees.length - unconfirmedEmployees.length;

    const sumEndpointTotalTons = (endpoints) =>
      endpoints
        .filter((data) => data)
        .reduce((sum, { totalTons }) => totalTons + sum, 0);

    const findCurrentScheduleMonthlyEmissions = ({ commuteSchedules }) => {
      const defaultReturnObj = {
        monthlyUtilitiesEmissionsVolume: 0,
        monthlyCommuteEmissionsVolume: 0
      };

      if (!commuteSchedules?.length) {
        return defaultReturnObj;
      }

      const schedulesWithEndpoints = commuteSchedules.filter(
        ({ commuteEndpoints }) => commuteEndpoints?.length
      );

      const [currentSchedule] = schedulesWithEndpoints;
      if (!currentSchedule) {
        return defaultReturnObj;
      }
      const { home, commuteEndpoints } = currentSchedule;
      if (!home) {
        return defaultReturnObj;
      }

      const monthlyUtilitiesEmissionsTonsArray =
        calcUtilitiesFromScheduleEndpoints({ commuteEndpoints, ...home });

      const monthlyUtilitiesEmissionsTons = sumEndpointTotalTons(
        monthlyUtilitiesEmissionsTonsArray
      );

      const { address } = home;
      const monthlyCommuteEmissionsTonsArray =
        findScheduleEndpointsCommuteEmissionsTons({
          commuteEndpoints,
          employeeAddress: address
        });

      const monthlyCommuteEmissionsTons = sumEndpointTotalTons(
        monthlyCommuteEmissionsTonsArray
      );

      return {
        monthlyUtilitiesEmissionsVolume: convertCarbonUnits(
          monthlyUtilitiesEmissionsTons
        ),
        monthlyCommuteEmissionsVolume: convertCarbonUnits(
          monthlyCommuteEmissionsTons
        )
      };
    };

    const findOnLeaveEmployeesMostRecentEmissions = (employee) => {
      const { monthlyUtilitiesEmissionsVolume, monthlyCommuteEmissionsVolume } =
        findCurrentScheduleMonthlyEmissions(employee);

      const totalMonthlyVolume =
        monthlyCommuteEmissionsVolume + monthlyUtilitiesEmissionsVolume;

      return {
        ...employee,
        mostRecentEmissionsVolume: totalMonthlyVolume,
        totalMonthlyVolume
      };
    };

    const balanceEmployeeEmissions = [
      ...confirmedEmployees,
      ...incompleteEmployees
    ].map((employee) => {
      const { id } = employee;

      const { monthlyUtilitiesEmissionsVolume, monthlyCommuteEmissionsVolume } =
        findCurrentScheduleMonthlyEmissions(employee);

      const { aggregatedTransactions = [] } =
        employeesAggregatedTransactions.find(
          ({ individualId }) => individualId === id
        ) || {};

      const employeeTransactions =
        splitAggregatedTransactionsBreakdownSubcategories(
          aggregatedTransactions
        );

      const calcTransactionsAverageVolumeBySubcategory = (subcategory) =>
        convertCarbonUnits(
          calcAvgEmissionsPastSixMonths(
            employeeTransactions.filter(
              (transaction) => transaction.subcategory === subcategory
            )
          )
        );

      const flightsTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("flights");
      const rentalCarTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("rental-cars");
      const ridesTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("rides");
      const mileageTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("company-vehicles");
      const fuelTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("fuel");
      const hotelTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("hotels");
      const trainsTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("trains");
      const equipmentFuelTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("equipment-fuel");
      const shippingTransactionsAverage =
        calcTransactionsAverageVolumeBySubcategory("shipping");

      const transactionsAverageMonthlyEmissionsVolume = convertCarbonUnits(
        calcAvgEmissionsPastSixMonths(employeeTransactions)
      );

      const averageMonthlyNonCommuteEmissionsVolume =
        transactionsAverageMonthlyEmissionsVolume +
        monthlyUtilitiesEmissionsVolume;

      return {
        ...employee,
        averageMonthlyNonCommuteEmissionsVolume,
        monthlyUtilitiesEmissionsVolume,
        monthlyCommuteEmissionsVolume,
        avgMonthlyFlightsEmissions: flightsTransactionsAverage,
        avgMonthlyRentalCarEmissions: rentalCarTransactionsAverage,
        avgMonthlyRidesEmissions: ridesTransactionsAverage,
        avgMonthlyMileageEmissions: mileageTransactionsAverage,
        avgMonthlyFuelEmissions: fuelTransactionsAverage,
        avgMonthlyHotelEmissions: hotelTransactionsAverage,
        avgMonthlyTrainsEmissions: trainsTransactionsAverage,
        avgMonthlyEquipmentFuelEmissions: equipmentFuelTransactionsAverage,
        avgMonthlyShippingEmissions: shippingTransactionsAverage,
        totalMonthlyVolume:
          monthlyCommuteEmissionsVolume +
          monthlyUtilitiesEmissionsVolume +
          transactionsAverageMonthlyEmissionsVolume
      };
    });

    const onLeaveEmployeeEmissions = onLeaveEmployees.map((employee) =>
      findOnLeaveEmployeesMostRecentEmissions(employee)
    );

    const defaultEmployeesCount = employeeCount
      ? employeeCount - notUnconfirmedEmployeesCount
      : unconfirmedEmployees.length;

    const { defaultEmissions } = emissionsMonths[0] || {};
    const { monthlyEmployeesEmissionsTons, totalMonthlyTons } =
      defaultEmissions || {};

    const unconfirmedEmployeeDefaultVolume = convertCarbonUnits(
      (monthlyEmployeesEmissionsTons || totalMonthlyTons) /
        defaultEmployeesCount
    );

    const unconfirmedEmployeesDefaultEmissions = unconfirmedEmployees.map(
      (employee) => ({
        ...employee,
        defaultTotalEmissions: unconfirmedEmployeeDefaultVolume,
        totalMonthlyVolume: unconfirmedEmployeeDefaultVolume
      })
    );

    return [
      ...terminatedEmployees.map((employee) => ({
        ...employee,
        totalMonthlyVolume: 0
      })),
      ...onLeaveEmployeeEmissions,
      ...unconfirmedEmployeesDefaultEmissions,
      ...balanceEmployeeEmissions
    ].map((employee) => {
      const { status } = employee;
      const { severity } = employeeStatuses[status];

      return { ...employee, severity };
    });
  };
};

export const editMostRecentSchedule = (commuteSchedules, date) =>
  commuteSchedules.map((schedule, idx) =>
    idx === 0
      ? { endDate: dayjs(date).subtract(1, "day").toDate(), ...schedule }
      : schedule
  );

export const useTerminateEmployee = () => {
  const { updateCollectionDoc } = useCachedFirebaseCrud();

  return (employee, date) => {
    const { id, commuteSchedules, links } = employee;
    const companyId = getAccountId();

    const updatedLinks = links.map((link) =>
      link.id !== companyId
        ? link
        : {
            ...link,
            status: "inactive",
            employeeStatus: "terminated",
            endDate: date
          }
    );

    return updateCollectionDoc("employees", id, {
      commuteSchedules: [...editMostRecentSchedule(commuteSchedules, date)],
      links: updatedLinks,
      endDate: date
    });
  };
};
